import { omitDeepUndefined } from '@mfw/components';

import type {
  FetchSignedUploadUrlArgs,
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type {
  ApiOrganizationSalesContact,
  UiOrganizationSalesContact,
  ApiOrganization,
  UiOrganization,
} from '../schemas/organizations.schema';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type { Maybe } from '../types/maybe.types';

import {
  type UpdateOrganizationBusinessDetailsVisibilityArgs,
  type UpdateOrganizationBusinessDetailsArgs,
  type FetchOrganizationSalesContactArgs,
  type UpdateOrganizationCategoryTagsArgs,
  type UpdateOrganizationClientTermsArgs,
  type UpdateOrganizationModulesArgs,
  type UpdateOrganizationTrainerTermsArgs,
  type UpdateOrganizationBasicInfoArgs,
  type UpdateOrganizationContactArgs,
  type UpdateOrganizationSocialArgs,
  type SetPreferredOrganizationArgs,
  type FetchPublicOrganizationsArgs,
  type UpdateOrgRebrandThemeArgs,
  type DeleteOrganizationByIdArgs,
  type FetchOrganizationByIdArgs,
  type EditOrganizationByIdArgs,
  type CreateOrganizationArgs,
  type FetchOrganizationsArgs,
} from './types/organizations.types';
import {
  mapOrganizationSalesContactFromApi,
  createUpdateOrganization,
  mapOrganizationFromApi,
} from './mappers/organizations.mappers';
import { postWithPagination, destroy, patch, post, get, put } from './axios';

// F8 — self-service leave/close primitives. F9 — both accept an optional
// content-transfer target (moves everything the caller authored in this org
// into `contentTargetOrganizationId` BEFORE disconnect/close); close also
// accepts the F10 ex-client-content-access decision, written to every
// client of the org being closed (default true = unchanged prior behavior).
export interface LeaveOrganizationArgs {
  contentTargetOrganizationId?: string;
  contentCopyTargetOrganizationId?: string;
  organizationId: string;
}

export interface CloseOrganizationArgs {
  contentTargetOrganizationId?: string;
  contentCopyTargetOrganizationId?: string;
  allowClientContentAccess?: boolean;
  organizationId: string;
}

export async function leaveOrganization({
  contentCopyTargetOrganizationId,
  contentTargetOrganizationId,
  organizationId,
}: LeaveOrganizationArgs) {
  await post(`/v2/organizations/${organizationId}/leave`, {
    contentCopyTargetOrganizationId,
    contentTargetOrganizationId,
  });
}

export async function closeOrganization({
  allowClientContentAccess,
  contentCopyTargetOrganizationId,
  contentTargetOrganizationId,
  organizationId,
}: CloseOrganizationArgs) {
  await post(`/v2/organizations/${organizationId}/close`, {
    allowClientContentAccess,
    contentCopyTargetOrganizationId,
    contentTargetOrganizationId,
  });
}

export async function fetchOrganizations({
  filters,
}: FetchOrganizationsArgs): Promise<
  BaseApiPaginatedResponse<UiOrganization[]>
> {
  const response = await get<BaseApiPaginatedResponse<ApiOrganization[]>>(
    '/v2/organizations',
    {
      query: {
        ...filters,
        // TODO Temporary, remove when api accepts status as lowercase
        ...(filters?.status ? { status: filters.status.toUpperCase() } : {}),
      },
    },
  );

  return {
    data: response?.data?.flatMap((d) =>
      mapOrganizationFromApi(d),
    ) as UiOrganization[],
    message: response?.message || '',
    status: response?.status || 200,
    total: response?.total || 0,
  };
}

export async function fetchPublicOrganizations({
  filters,
}: FetchPublicOrganizationsArgs) {
  const response = await postWithPagination<ApiOrganization[]>(
    '/v2/public/organizations',

    {
      ...filters,
      // TODO Temporary, remove when api accepts status as lowercase
      ...(filters?.status ? { status: filters.status.toUpperCase() } : {}),
      ...(filters?.ids ? { ids: filters.ids } : {}),
    },
  );

  return {
    data: response?.data?.flatMap((d) => mapOrganizationFromApi(d)),
    message: response?.message || '',
    status: response?.status || 200,
    total: response?.total || 0,
  };
}

export async function fetchSwitcherOrganizations({
  filters,
}: FetchOrganizationsArgs): Promise<
  BaseApiPaginatedResponse<UiOrganization[]>
> {
  const response = await get<BaseApiPaginatedResponse<ApiOrganization[]>>(
    '/v2/auth/me/organizations',
    {
      query: {
        ...filters,
        // TODO Temporary, remove when api accepts status as lowercase
        ...(filters?.status ? { status: filters.status.toUpperCase() } : {}),
      },
    },
  );

  return {
    data: response?.data?.flatMap((d) =>
      mapOrganizationFromApi(d),
    ) as UiOrganization[],
    message: response?.message || '',
    status: response?.status || 200,
    total: response?.total || 0,
  } as BaseApiPaginatedResponse<UiOrganization[]>;
}

export async function fetchOrganizationById({
  id,
}: FetchOrganizationByIdArgs): Promise<Maybe<UiOrganization>> {
  const response = await get<BaseApiResponse<ApiOrganization>>(
    `/v2/organizations/${id}`,
  );
  return mapOrganizationFromApi(response?.data);
}

export const fetchOrganizationSignedProfileUrl = async ({
  mediaType,
  organizationId,
}: { organizationId: string } & FetchSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/organizations/${organizationId}/signed-upload-organization-logo?mediaType=${mediaType}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export async function fetchPublicOrganizationById({
  id,
}: FetchOrganizationByIdArgs): Promise<Maybe<UiOrganization>> {
  const response = await get<BaseApiResponse<ApiOrganization>>(
    `/v2/public/organizations/${id}`,
  );
  return mapOrganizationFromApi(response?.data);
}

export async function deleteOrganizationById({
  id,
}: DeleteOrganizationByIdArgs) {
  await destroy(`/v2/organizations/${id}`);
  return true;
}

export async function createOrganization({
  type,
  ...args
}: CreateOrganizationArgs) {
  return post(`/v2/organizations`, {
    ...createUpdateOrganization(args),
    type,
  });
}

export async function editOrganizationById({
  id,
  ...args
}: EditOrganizationByIdArgs) {
  const allSettings = createUpdateOrganization(args);
  const response = await patch<ApiOrganization>(
    `/v2/organizations/${id}`,
    omitDeepUndefined(allSettings),
  );
  return mapOrganizationFromApi(response?.data);
}

/**
 * Rebrand (F6): persists the org's editable theme overrides. Body is the
 * Record of editable var names → 6-digit hex; the BE gates on
 * `rebrandActive` (403) and validates the payload (422).
 */
export async function updateOrgRebrandTheme({
  organizationId,
  theme,
}: UpdateOrgRebrandThemeArgs) {
  await put(`/v2/organizations/${organizationId}/rebrand-theme`, theme);
}

export async function updateOrganizationCategoryTags({
  organizationId,
  tagIds,
}: UpdateOrganizationCategoryTagsArgs) {
  await patch(`/v2/organizations/${organizationId}/category-tags`, {
    tagIds,
  });
}

/**
 * Organization Profile v2 — saves the "Basic information" section (name,
 * logo, About us, organization type, expertise) in one call. Answers 409
 * `NAME_TAKEN` when another organization in the tenant holds the name. The
 * caller refetches the organization.
 */
export async function updateOrganizationBasicInfo({
  organizationId,
  data,
}: UpdateOrganizationBasicInfoArgs) {
  await put(`/v2/organizations/${organizationId}/profile/basic`, data);
}

/**
 * 6.10 — switches "client accepts terms on connecting" on or off. Turning it
 * on is refused with a 400 until the organization has saved its Terms of Use.
 * The caller refetches the organization.
 */
export async function updateOrganizationClientTerms({
  organizationId,
  data,
}: UpdateOrganizationClientTermsArgs) {
  await put(`/v2/organizations/${organizationId}/profile/client-terms`, data);
}

/**
 * Switches "trainers may have their own terms" on or off
 * (`PUT /organizations/:id/profile/trainer-terms`). The caller refetches the
 * organization.
 */
export async function updateOrganizationTrainerTerms({
  organizationId,
  data,
}: UpdateOrganizationTrainerTermsArgs) {
  await put(`/v2/organizations/${organizationId}/profile/trainer-terms`, data);
}

/**
 * Organization Profile v2 — saves the "Contact & legal" section (e-mail,
 * phone, legal address, default currency, tax id, registration number) in
 * one call. A phone number held by someone else is refused with a 400. The
 * caller refetches the organization.
 */
export async function updateOrganizationContact({
  organizationId,
  data,
}: UpdateOrganizationContactArgs) {
  await put(`/v2/organizations/${organizationId}/profile/contact`, data);
}

/**
 * Organization Profile v2 — saves the "Social & marketing" section (website
 * and social links, each with its own public flag) in one call. The caller
 * refetches the links.
 */
export async function updateOrganizationSocial({
  organizationId,
  data,
}: UpdateOrganizationSocialArgs) {
  await put(`/v2/organizations/${organizationId}/profile/social`, data);
}

/**
 * Organization Profile v2 — saves the "Business details" section (working
 * hours, locations, price list or pricing-page link, sales contact, and the
 * section's one public flag) in one call. The caller refetches the public
 * page reads.
 */
export async function updateOrganizationBusinessDetails({
  organizationId,
  data,
}: UpdateOrganizationBusinessDetailsArgs) {
  await put(`/v2/organizations/${organizationId}/profile/business`, data);
}

/**
 * Organization Profile v2 — makes the whole "Business details" section public
 * or private without resending its content (the Public page panel's switch).
 */
export async function updateOrganizationBusinessDetailsVisibility({
  organizationId,
  isPublic,
}: UpdateOrganizationBusinessDetailsVisibilityArgs) {
  await put(`/v2/organizations/${organizationId}/profile/business/visibility`, {
    isPublic,
  });
}

/** The Business details section's sales contact — `null` when none is set. */
export async function fetchOrganizationSalesContact({
  organizationId,
}: FetchOrganizationSalesContactArgs): Promise<UiOrganizationSalesContact | null> {
  const response = await get<
    BaseApiResponse<Maybe<ApiOrganizationSalesContact>>
  >(`/v2/organizations/${organizationId}/profile/sales-contact`);
  return response?.data
    ? mapOrganizationSalesContactFromApi(response.data)
    : null;
}

/*
 * This function sets the preferred organization (or the org-less personal
 * profile, via `context: 'personal'`) for the current user.
 */
export async function setPreferredOrganization({
  id,
  context,
}: SetPreferredOrganizationArgs) {
  const response = await patch<ApiOrganization>(
    `/v2/auth/me/set-preferred-organization`,
    {
      context,
      organizationId: id,
    },
  );
  return mapOrganizationFromApi(response?.data);
}

/**
 * Shows or hides the Desk and Keys modules for one organization
 * (`PUT /organizations/:id/profile/modules`). Super admin only. The caller
 * refetches the organization.
 */
export async function updateOrganizationModules({
  organizationId,
  data,
}: UpdateOrganizationModulesArgs) {
  await put(`/v2/organizations/${organizationId}/profile/modules`, data);
}
